import { action, computed, makeObservable, observable } from 'mobx';

export const ASYNC_OPERATION_DEFAULT_TIMEOUT = 1000 * 60 * 5; // 5 minutes

export type AsyncOperationStoreOptions = {
	requestId: string;
	taskIds: number[];
	name?: string;
	timeout?: number;
	isExpanded?: boolean;
};

export abstract class AsyncOperationStore<TFailure extends { id: string | number }> {
	fullyCompleted = new Map<string | number, boolean>();
	failuresMap = new Map<string | number, TFailure>();
	protected name: string;
	protected readonly requestId: string;
	protected readonly taskIds: number[];
	protected readonly timeout: number;
	protected readonly realtimeAsyncOperationAbortController = new AbortController();
	hideFailureReasonColumn = false;
	isExpanded: boolean;

	protected constructor(options: AsyncOperationStoreOptions) {
		this.timeout = options.timeout ?? ASYNC_OPERATION_DEFAULT_TIMEOUT;
		this.requestId = options.requestId;
		this.name = options.name;
		this.taskIds = options.taskIds;
		this.isExpanded = options.isExpanded ?? false;
		makeObservable(this, {
			fullyCompleted: observable,
			failuresMap: observable,
			failures: computed,
			successfulItemsCount: computed,
			totalNumberOfTasks: computed,
			dispose: action
		});
	}

	abstract dispose(): void;

	get failures() {
		return Array.from(this.failuresMap.values());
	}

	get successfulItemsCount() {
		return Array.from(this.fullyCompleted.values()).filter(Boolean).length;
	}

	get totalNumberOfTasks() {
		return this.taskIds.length;
	}

	get id() {
		return this.requestId;
	}

	get displayName() {
		return this.name;
	}

	protected setupAsyncOperationTimeout(onTimeout: (pendingTaskIds: number[]) => Promise<void>) {
		const timeout = setTimeout(async () => {
			this.realtimeAsyncOperationAbortController.signal.removeEventListener('abort', clearRealTimeTimeout);
			this.realtimeAsyncOperationAbortController.abort();

			const pendingTasks = this.taskIds.filter(id => !this.fullyCompleted.has(id));
			await onTimeout(pendingTasks);
		}, this.timeout);

		const clearRealTimeTimeout = clearTimeout.bind(undefined, timeout);
		this.realtimeAsyncOperationAbortController.signal.addEventListener('abort', clearRealTimeTimeout, {
			once: true
		});
	}
}
